import React, { useEffect, useState } from 'react';
import { Hub } from 'aws-amplify';
import { useLocation } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';

import HeaderBar from './HeaderBar';
import IconRail from './IconRail';
import { getNavItems, getActiveNavItem } from './NavigationListItems';

import useLogout from '../../hooks/useLogout';
import { fetchWhiteListedFileTypes } from '../../app/store/fileSlice';
import StandardDialog, { StandardDialogActions } from '../../features/Modals/StandardDialog';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setShowTokenExpiredModal } from '../../app/store/userSlice';
import getAuthSession, { MAX_SESSION_LENGTH } from '../../services/auth';

const Shell = styled('div')({ display: 'flex', flexDirection: 'column', height: '100vh' });
const Body = styled('div')({ display: 'flex', flex: 1, minHeight: 0 });

/**
 * The site-wide navigation shell: a fixed top header bar and a fixed 90px icon
 * rail wrapping the page content. Also hosts the global chrome that used to live
 * in the drawer — the token-expiry modal, its `Hub` listener, and the
 * session-expiry trigger.
 */
export function SideNav({ children }: { children: React.ReactNode }) {
  const { user } = useAppSelector((state) => state.user);
  const showTokenExpiredModal = useAppSelector(
    (state) => state.user?.showTokenExpiredModal ?? false,
  );

  const dispatch = useAppDispatch();
  const handleLogout = useLogout();
  const location = useLocation();

  const [expirationTime, setExpirationTime] = useState<number | null>(null);

  const items = getNavItems(user);
  const activeItem = getActiveNavItem(items, location.pathname);

  const logOutUser = () => {
    handleLogout();
    dispatch(setShowTokenExpiredModal(false));
  };

  // Whitelisted file types + token-refresh-failure modal (kept from the drawer).
  useEffect(() => {
    dispatch(fetchWhiteListedFileTypes());

    const unsubscribe = Hub.listen('auth', ({ payload: { event } }) => {
      if (event !== 'tokenRefresh_failure') return;
      dispatch(setShowTokenExpiredModal(true));
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Session-expiry tracking. The live countdown is no longer displayed, but the
  // 24h max-session logout and the expiry modal trigger are preserved.
  const updateExpirationTime = async () => {
    try {
      const authSession = await getAuthSession();
      const accessToken = authSession.getAccessToken();
      const newExpiration = accessToken.getExpiration() * 1000;

      const loginTimeStr = localStorage.getItem('fixedLoginTime');
      if (!loginTimeStr) {
        const payload = accessToken.decodePayload?.();

        if (!payload?.iat) {
          console.warn('Access token missing "iat" field. Forcing logout.');
          await handleLogout();
          return;
        } else {
          const issuedAt = payload.iat * 1000;
          localStorage.setItem('fixedLoginTime', issuedAt.toString());
        }
      }

      const finalLoginTimeStr = localStorage.getItem('fixedLoginTime');
      const loginTime = finalLoginTimeStr ? parseInt(finalLoginTimeStr, 10) : Date.now();
      const sessionExpiresAt = loginTime + MAX_SESSION_LENGTH;

      if (Date.now() > sessionExpiresAt) {
        console.warn('Session exceeded 24 hours — logging out.');
        await handleLogout();
        return;
      }

      if (newExpiration !== expirationTime && !showTokenExpiredModal) {
        setExpirationTime(newExpiration);
      }
    } catch (error) {
      console.error('Error fetching session:', error);
    }
  };

  useEffect(() => {
    updateExpirationTime();
    const refreshInterval = setInterval(updateExpirationTime, 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    if (!expirationTime) return;

    const remaining = expirationTime - Date.now();
    if (remaining <= 0) {
      dispatch(setShowTokenExpiredModal(true));
      return;
    }

    const timeoutId = setTimeout(() => {
      dispatch(setShowTokenExpiredModal(true));
    }, remaining);

    return () => clearTimeout(timeoutId);
  }, [expirationTime, dispatch]);

  return (
    <Shell>
      <HeaderBar activeItem={activeItem} />
      <Body>
        <IconRail items={items} activeId={activeItem?.id} />
        {children}
      </Body>
      <StandardDialog cantClose={true} title="Your Login Has Expired" handleClose={() => { return; }} isOpen={showTokenExpiredModal}>
        <Typography>Your login has expired. Please click the button below to login again.</Typography>
        <StandardDialogActions>
          <Button variant="contained" onClick={() => logOutUser()}>Take me to the Login Form</Button>
        </StandardDialogActions>
      </StandardDialog>
    </Shell>
  );
}
